import { createApp } from 'vue'
import mapMixins from './maps.js'
import { cloneDeep } from 'lodash'
import swal from 'sweetalert'
const { Map } = await google.maps.importLibrary('maps')
const { LatLngBounds } = await google.maps.importLibrary('core')
const { AdvancedMarkerElement } = await google.maps.importLibrary('marker')

window.pawine = window.pawine || {}

/**
 * Create a custom event to open a winery's profile.
 *
 * @param  {Boolean}  isLoading
 * @return void
 */
window.pawine.openProfile = function(wineryId) {
  const event = new CustomEvent('openProfile', {
    detail: {
      wineryId,
    }
  })
  window.dispatchEvent(event)
}

let map

const app = createApp({
  mixins: [mapMixins],
  data() {
    return {
      routes: window.pawine.routes || {},
      wineries: window.pawine.wineries || [],
      mapMarkers: [],
      filteredWineries: [],
      isLoading: false,
      isGeolocating: false,
      searchLocation: {
        postalCode: null,
        latitude: null,
        longitude: null,
      },
      filterSelections: {
        postalCode: null,
        radius: 25,
        city: null,
        winery: null,
      },
      searchForm: {
        postalCode: null,
        radius: 25,
        city: '',
        winery: '',
      },
    }
  },
  computed: {
    cities() {
      let cities = this.wineries.map(winery => winery.city)

      // Remove duplicates and sort alphabetically.
      return [... new Set(cities)].sort()
    }
  },
  async mounted() {
    this.wineries = await this.loadWineries()
    this.filteredWineries = this.wineries

    this.initMap()
  },
  methods: {
    async loadWineries() {
      this.isLoading = true
      const resp = await axios.get(this.routes.loadWineries)
      this.isLoading = false

      return resp.data
    },
    async initMap() {
      const position = { lat: 40.913410, lng: -77.764748 }

      map = new Map(document.getElementById('map'), {
        zoom: 7,
        center: position,
        mapId: window.pawine.googleMapsId,
      })

      this.populateMap()
      this.addCurrentLocationToMap()
    },
    populateMap() {

      // First remove any existing markers.
      this.mapMarkers.forEach(marker => {
        google.maps.event.removeListener(marker.listener)
        marker.marker.setMap(null)
      })

      this.mapMarkers = []

      // Then add a new marker for each winery.
      this.filteredWineries.forEach(winery => {
        if (winery.latitude && winery.longitude) {

          let pinSvgString

          if (winery.isMember) {
            pinSvgString = window.pawine.img.memberMapPin
          } else {
            pinSvgString = window.pawine.img.nonMemberMapPin
          }

          const parser = new DOMParser()
          const pinSvg = parser.parseFromString(
            pinSvgString,
            "image/svg+xml",
          ).documentElement

          const marker = new AdvancedMarkerElement({
            map,
            position: { lat: Number(winery.latitude), lng: Number(winery.longitude) },
            title: winery.name,
            content: pinSvg,
          })

          const listener = marker.addListener('click', ({ domEvent, latLng }) => {
            const { target } = domEvent

            // Open info window.
            this.mapInfoWindow.close()
            this.mapInfoWindow.setContent(
              '<div class="map-ui-info-window">' + 
                '<b>' + winery.name + '</b><br>' + 
                winery.address + '<br>' + 
                winery.city + ', ' + winery.state + ' ' + winery.postalCode + '<br>' + 
                '<button type="button" onclick="window.pawine.openProfile(' + winery.id + ')">View Profile</button>' + 
              '</div>'
            )
            this.mapInfoWindow.open(marker.map, marker)

            // Scroll the winery into view within the search results panel.
            const locationResultId = 'winery-result-' + (winery.wineryId ? `${winery.wineryId}-${winery.id}` : winery.id)
            const locationResult = document.getElementById(locationResultId)
            const resultsContainer = document.getElementById('results')
            const scrollToPosition = locationResult.offsetTop - resultsContainer.scrollTop

            resultsContainer.scrollBy({top: scrollToPosition, left: 0, behavior: 'smooth'})
          })

          this.mapMarkers.push({
            marker,
            listener,
            winery,
          })
        }
      })

      // Recenter and zoom on markers.
      const bounds = new LatLngBounds()

      this.filteredWineries.forEach((winery) => {
        if (winery.latitude && winery.longitude) {
          bounds.extend({ lat: Number(winery.latitude), lng: Number(winery.longitude) })
        }
      })

      map.fitBounds(bounds)
    },
    async search() {
      this.filterSelections.postalCode = this.searchForm.postalCode
      this.filterSelections.city = this.searchForm.city
      this.filterSelections.winery = this.searchForm.winery

      this.filteredWineries = await this.filterWineries()
      this.populateMap()

      // Store the search.
      axios.post(this.routes.saveSearch, {
        ui: 'winery-locator',
        parameters: this.filterSelections,
      })
    },
    async clearFilters() {
      this.searchForm.postalCode = null
      this.searchForm.city = ''
      this.searchForm.winery = ''

      this.filterSelections.postalCode = null
      this.filterSelections.city = null
      this.filterSelections.winery = null

      this.filteredWineries = await this.filterWineries()
    },
    async filterWineries() {
      let filtered = cloneDeep(this.wineries)

      // Postal code search
      if (this.filterSelections.postalCode) {

        if (
          this.filterSelections.postalCode != this.searchLocation.postalCode || (
            ! this.searchLocation.latitude || 
            ! this.searchLocation.longitude
          )
        ) {
          await this.getPostalCodeCoords(this.filterSelections.postalCode)
        }

        filtered = filtered.filter(winery => {

          if (! winery.latitude || ! winery.longitude) {
            return false
          }

          // Calculate the distance, in miles, between the provided postal code 
          // and the ad's postal code.
          const distance = 
            this.radiansToDegrees(Math.acos(
              Math.sin(this.degreesToRadians(winery.latitude)) *
              Math.sin(this.degreesToRadians(this.searchLocation.latitude))
              + Math.cos(this.degreesToRadians(winery.latitude))
              * Math.cos(this.degreesToRadians(this.searchLocation.latitude))
              * Math.cos(this.degreesToRadians(winery.longitude - this.searchLocation.longitude))
            )) * 69.06

          return distance <= this.searchForm.radius
        })
      
      // City search
      } else if (this.filterSelections.city) {
        filtered = filtered.filter(winery => {
          return winery.city == this.filterSelections.city
        })

      // Winery search
      } else if (this.filterSelections.winery) {
        filtered = filtered.filter(winery => {
          return winery.id == this.filterSelections.winery ||
                 winery.wineryId == this.filterSelections.winery
        })
      }

      return filtered
    },
    async addCurrentLocationToMap() {
      await this.getUserCoords()

      if (this.userCoords.latitude && this.userCoords.longitude) {
        let pinSvgString = window.pawine.img.currentLocationMapPin

        const parser = new DOMParser()
        const pinSvg = parser.parseFromString(
          pinSvgString,
          "image/svg+xml",
        ).documentElement

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: Number(this.userCoords.latitude), lng: Number(this.userCoords.longitude) },
          title: 'Current Location',
          content: pinSvg,
        })

        const listener = marker.addListener('click', ({ domEvent, latLng }) => {
          const { target } = domEvent

          this.mapInfoWindow.close()
          this.mapInfoWindow.setContent(
            '<b>Current Location</b>'
          )
          this.mapInfoWindow.open(marker.map, marker)
        })

        this.mapMarkers.push({
          marker,
          listener,
        })
      }
    },
  }
})

app.mount('#map-ui')
